<template>
  <PinturaEditor ref="editor"
                 v-if="created && imageData && !generating"
                 v-bind="editorDefaults"
                 :src="imageData"
                 :enableButtonExport="false"
                 :enableButtonClose="false"
                 :willRenderToolbar="willRenderToolbar"
                 :willRenderUtilTabs="willRenderUtilTabs"
  ></PinturaEditor>
  <b-container class="d-flex justify-content-center align-items-center text-center" v-else-if="generating">
    <div>
      <h3>{{ $tr('generating', 'Generating') }}
        <font-awesome-icon
            :icon="['fad', 'spinner-third']"
            spin
            style="--fa-primary-color: #3584e4; --fa-secondary-color: #3584e4;"
            class="ml-4 mt-2"/>
      </h3>
      <div class="progress mt-4" style="height: 20px; width: 100%; min-width: 400px">
        <div
            class="progress-bar"
            role="progressbar"
            :style="{ width: progressPercentage + '%', transition: 'width 0.2s ease' }"
            aria-valuenow="progressPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
        >
          {{ progressPercentage }}%
        </div>
      </div>
    </div>
  </b-container>
</template>

<script>

import VBox from "@/components/VBox";
import {
  appendDefaultEditor,
  createDefaultColorOptions,
  createMarkupEditorToolStyle,
  createMarkupEditorToolStyles,
} from 'pintura';

let ctr = 0;
// Import the editor default configuration
import {getEditorDefaults, createNode} from 'pintura';

// Import the component from `vue-pintura`
import {PinturaEditor} from 'vue-pintura';

import {
  createDefaultImageReader,
  createDefaultImageWriter,

  // The method used to register the plugins
  setPlugins,

  // The plugins we want to use
  plugin_crop,
  plugin_finetune,
  plugin_annotate,

  // The user interface and plugin locale objects
  locale_en_gb,
  plugin_crop_locale_en_gb,
  plugin_finetune_locale_en_gb,
  plugin_annotate_locale_en_gb,

  // Because we use the annotate plugin we also need
  // to import the markup editor locale and the shape preprocessor
  markup_editor_locale_en_gb,
  createDefaultShapePreprocessor,

  // Import the default configuration for the markup editor and finetune plugins
  markup_editor_defaults,
  plugin_finetune_defaults,
} from 'pintura';
import DialogManager from "@/utils/DialogManager";
import Rpc from "@/Rpc";
import ActivityManager from "@/activity/ActivityManager";
import {sendEvent} from "@/utils/GEvents";
import {sketch2Photo, draft2Project, AISketch} from "@/components/img/AIService";

export default {
  name: "ImageEditor",
  components: {VBox, PinturaEditor},
  async mounted() {
    setTimeout(() => {
      this.created = true;
    });

    if (!this.checkIsCordova) {
      this.imageData = this.src;
    } else {
      await this.loadImageFromUrl();
      window["_pinturaImgBlob"] = await this.b64toBlob(this.imageData.split(',')[1]);
    }

  },
  computed: {
    checkIsCordova() {
      return window.isCordova
    },
    progressPercentage() {
      return Math.min(this.progressValue * 10, 100);
    },
    addCordovaUrl() {
      return window.cordovaUrl
    }
  },
  methods: {

    b64toBlob(b64Data, contentType = '', sliceSize = 512) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }

      const blob = new Blob(byteArrays, {type: contentType});
      return blob;
    },

    /**
     * Konwertuje obraz z URL na dane base64.
     * @param {string} imageUrl URL obrazu
     * @returns {Promise<string>} Obraz jako dane base64
     */
    printFile(file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        console.log(evt.target.result);
      };
      reader.readAsText(file);
    },

    urlToBase64(url) {
      return new Promise((resolve, reject) => {
        cordova.plugin.http.sendRequest(
            url,
            {method: 'get', params: {}, headers: {}, responseType: 'Blob'},
            function (response) {
              // console.log("cordova success", response);
              resolve(response.data);
            },
            function (response) {
              console.log("cordova failed", response);
              reject(response);
            }
        );
      });
    },

    async AIPaint() {
      let prompt = await DialogManager.lineInput($tr('pintura.generate', 'AI Generate'),
          $tr('pintura.prompt', 'Type a prompt for AI to generate new image'));
      if (prompt) {
        let file = await this.save();
        let newImageFile = await Rpc.BusinessEntities.uploadImage(file, "").lock();
        //Sam link + prompt
      }

    },

    async onSketch2Photo() {
      this.generating = true;
      this.progressValue = 0;

      const newImageData = await sketch2Photo(null,{
        saveImage: this.save,
        onProgress: (progress) => {
          this.progressValue = progress;
        },
      });

      if (newImageData) {
        this.imageData = newImageData;
      }
      this.generating = false;
    },

    async onDraft2Project() {
      this.generating = true;
      this.progressValue = 0;

      const newImageData = await draft2Project({
        saveImage: this.save,
        onProgress: (progress) => {
          this.progressValue = progress;
        },
      });

      if (newImageData) {
        this.imageData = newImageData;
      }
      this.generating = false;
    },

    async onAISketch() {
      this.generating = true;
      this.progressValue = 0;

      const newImageData = await AISketch({
        saveImage: this.save,
        onProgress: (progress) => {
          this.progressValue = progress;
        },
      });

      if (newImageData) {
        this.imageData = newImageData;
      }
      this.generating = false;
    },

    async loadImageFromUrl() {
      let blobData = await this.urlToBase64(this.src);

      function blobToBase64(blob) {
        return new Promise((resolve, _) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        });
      }

      var base64 = await blobToBase64(blobData)

      this.imageData = base64;
      window["_pinturaImg"] = this.imageData;

      setTimeout(() => {
        this.created = true;
      });
    },
    /**
     * @return {Promise<File>}
     */
    async save() {
      try {
        const imageWriterOutput = await this.getEditor().processImage();
        /** @type {File} */
        const file = imageWriterOutput.dest;
        return file;
      } catch (e) {
        console.log(e)
      }
    },

    /**
     * https://pqina.nl/pintura/docs/v8/api/
     * @return {PinturaEditor}
     */
    getEditor() {
      return this.$refs.editor.editor;
    }
  },
  props: {
    src: {
      required: true
    }
  },

  data() {
    let def = getEditorDefaults();
    def['markupEditorToolStyles']['text'][0]['fontSize'] = "4%";

    return {
      created: false,
      generating: false,
      imageData: null,
      progressValue: 0,
      canvas: null,
      // blobImage: null,
      // Pass the editor default configuration options
      editorDefaults: {
        ...def,
        util: "annotate",
        imageWriter: createDefaultImageWriter(),

        // This will result in a square crop
        // imageCropAspectRatio: 1,
      },

      willRenderUtilTabs: (toolbar, env, redraw) => {
        // console.log(toolbar)
      },

      willRenderToolbar: (toolbar, env, redraw) => {
        const buttonContainer = createNode(
            'div',
            'button-container',
            {
              style: 'display: flex; flex-direction: row; align-items: center; gap: 5px; margin-top: 20px;'
            },
            [
              // createNode(
              //     'Button',
              //     'my-button',
              //     {
              //       label: 'AI Retouch Guide',
              //       title: 'AI Retouch Guide',
              //       onclick: async () => {
              //         window['Guides'].AIInpaintTutorialFromView();
              //       },
              //       style: 'background-color: #2990ff; color: white; font-size: 16px; padding: 5px 20px; border-radius: 5px; font-weight: bold;'
              //     }
              // ),

              createNode(
                  'Button',
                  'my-second-button',
                  {
                    label: $tr('aiRetouch', 'AI Retouch'),
                    title: $tr('aiRetouch', 'AI Retouch'),
                    onclick: () => {
                      this.onAISketch();
                      window['Guides'].AIInpaintTutorialFromView();
                    },
                    style: 'background-color: rgb(82, 148, 179); color: white; font-size: 16px; padding: 8px 25px; border-radius: 5px; font-weight: bold;',
                    class: 'check-primary'
                  }
              ),

              createNode(
                  'Button',
                  'my-button-sketch',
                  {
                    label: $tr('aiSketch2Img', 'Sketch to photo'),
                    title: $tr('aiSketch2Img', 'Sketch to photo'),
                    onclick: () => {
                      this.onSketch2Photo();
//                      window['Guides'].AIInpaintTutorialFromView();
                    },
                    style: 'background-color: rgb(82, 148, 179); color: white; font-size: 16px; padding: 8px 25px; border-radius: 5px; font-weight: bold;',
                    class: 'check-primary'
                  }
              ),

//               createNode(
//                   'Button',
//                   'my-button-draft',
//                   {
//                     label: $tr('draft2Project', 'Draft to project'),
//                     title: $tr('draft2Project', 'Draft to project'),
//                     onclick: () => {
//                       this.onDraft2Project();
// //                      window['Guides'].AIInpaintTutorialFromView();
//                     },
//                     style: 'background-color: rgb(82, 148, 179); color: white; font-size: 16px; padding: 8px 25px; border-radius: 5px; font-weight: bold;',
//                     class: 'check-primary'
//                   }
//               )

            ]
        );


        return [
          ...toolbar,
          buttonContainer,
        ];
      },

    }
  }
}
</script>
<style>

.check-primary {
  width: 64px !important;
  max-width: 64px !important;
  height: 64px !important;
  max-height: 64px !important;
  padding: 5px !important;
  text-wrap: wrap !important;
  border-radius: 12px !important;
}

.check-primary:hover {
  background-color: #256477 !important;
}

.PinturaRoot {
  background-color: #ffffff !important;
}

.pintura-editor {

  /* ostylizować podobnie do bootstrapa */

  --color-background: 255, 255, 255;
  --color-foreground: 0, 0, 0;
  --color-focus: 0, 0, 200;
  --color-preview-outline: 0, 0, 200;

  --color-primary: #2990ff;
  --color-primary-dark: #1a80ec;
  --color-primary-text: #fff;
  --color-secondary: #03a9f4;
  --color-secondary-dark: #046bbf;

  --border-radius-round: 0.12em;
  --border-radius: 0.75em;
  --button-cursor: pointer;

  --font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto,
  Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji',
  'Segoe UI Symbol';
  --font-size: 16px;
}

@media screen and (max-width: 500px) {
  .progress {
    min-width: 300px !important;
  }
}
</style>